import { useMemo } from 'react';
import { Descendant, Text } from 'slate';
import {
  modeTranslate,
  overseaTranslate,
  TranslateMode,
  TranslateOptions,
} from 'services/api/overseaTranslate';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { ConceptElement, ParagraphElement } from 'components/TemplateEditor/slateTypes';
import {
  mustache2SlateValueWithCatch,
  SlateValue2Mustache,
} from 'components/TemplateEditor/mustacheUtils';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import { aideGathersMap, gatherScenariosMap } from '../../config';

export type ScenarioType =
  | AutoEmit.BaseTask['scenarioType']
  | Marketing.AllScenarioType
  | 'NIGHT_WATCH'
  | false
  | 'TODO';

export function getConcepts(
  _: General.PlatformUpperCase | false,
  scenarioType: ScenarioType | false,
  replyOption?: Marketing.ReplyOption,
): Concept[] {
  if (scenarioType === false || scenarioType === 'TODO') {
    return [];
  }

  const defaultConcepts: Concept[] = [
    {
      id: '买家昵称',
      label: '买家昵称',
    },
  ];

  if (!scenarioType || scenarioType === 'NIGHT_WATCH') {
    return defaultConcepts;
  }

  if (scenarioType === 'OUT_STOCK_REMINDER') {
    return [
      ...defaultConcepts,
      {
        id: '订单号',
        label: '订单号',
      },
      {
        id: '缺货商品名称',
        label: '缺货商品名称',
      },
      {
        id: '推荐商品说明',
        label: '推荐商品说明',
      },
    ];
  }

  if (scenarioType === 'ORDER_FOLLOWUP') {
    return [
      ...defaultConcepts,
      {
        id: '订单号',
        label: '订单号',
      },
      {
        id: '商品名称',
        label: '商品名称',
      },
      {
        id: '推荐商品说明',
        label: '推荐商品说明',
      },
    ];
  }

  if (scenarioType === 'SIGNAL_COUPON') {
    return [
      ...defaultConcepts,
      {
        id: '券码',
        label: '券码',
      },
    ];
  }

  if (scenarioType === 'RECEPTION_SCORE' && replyOption === 'DEFAULT') {
    return [
      ...defaultConcepts,
      {
        id: '好评',
        label: '好评',
      },
      {
        id: '差评',
        label: '差评',
      },
    ];
  }

  const concepts: Concept[] = [...defaultConcepts];

  concepts.push({
    id: '店铺名称',
    label: '店铺名称',
  });

  if (
    (['WELCOME_REPLY', 'TIMEOUT_REPLY', 'INVITE_FOLLOWER', 'ORDER_REMINDER'] as const).includes(
      scenarioType,
    )
  ) {
    return concepts;
  }

  concepts.push({
    id: '订单号',
    label: '订单号',
  });

  if (
    (
      [
        'PAYMENT_REMINDER',
        'PRAISE_REMINDER',
        'POSITIVE_REVIEW_CARE',
        'NEGATIVE_REVIEW_SAVE',
        'SIGN_REMINDER',
      ] as const
    ).includes(scenarioType) ||
    aideGathersMap.saveOrder.some((gather) =>
      gatherScenariosMap[gather].some((scenarios) => scenarios.includes(scenarioType)),
    )
  ) {
    return concepts;
  }

  concepts.push({
    id: '收货地址',
    label: '收货地址',
  });

  if ((['CONFIRM_TRADE', 'DELAY_SHIP'] as const).includes(scenarioType)) {
    if (scenarioType === 'CONFIRM_TRADE') {
      concepts.push({
        id: '商品发货天数',
        label: '商品发货天数',
      });
    }

    return concepts;
  }

  concepts.push({
    id: '物流单号',
    label: '物流单号',
  });

  if (scenarioType === 'SHIPPED_REMINDER') {
    return concepts;
  }

  concepts.push({
    id: '当前物流描述',
    label: '当前物流描述',
  });

  if ((['DELIVERY_REMINDER', 'WAIT_DELIVERY_AGAIN'] as const).includes(scenarioType)) {
    return concepts;
  }

  return defaultConcepts;
}

export function useGetConcepts(
  platform: General.PlatformUpperCase | false,
  scenarioType: ScenarioType | false,
  replyOption?: Marketing.ReplyOption,
  extraConcepts?: Concept[],
) {
  return useMemo(
    () => [...getConcepts(platform, scenarioType, replyOption), ...(extraConcepts || [])],
    [platform, scenarioType, replyOption, extraConcepts],
  );
}

export function translateTemplate(
  template: Descendant[],
  to: General.Language,
  recommendTemplates: Marketing.Policy['recommendTemplates'] = [],
  mode?: TranslateMode,
  options?: TranslateOptions,
) {
  if (recommendTemplates.length) {
    const mustacheTemplate = `#mustache#${SlateValue2Mustache(template)}`;

    const targetRecommendTemplates = recommendTemplates.find(({ multiLingualTemplates }) => {
      return multiLingualTemplates.some(({ replyTemplate }) => {
        const slateValue = mustache2SlateValueWithCatch(replyTemplate.replace(/^#mustache#/, ''));

        if (!slateValue) {
          return false;
        }

        const encodedTemplate = `#mustache#${SlateValue2Mustache(slateValue)}`;

        return encodedTemplate === mustacheTemplate;
      });
    });

    if (targetRecommendTemplates) {
      const { multiLingualTemplates } = targetRecommendTemplates;

      const targetRecommendTemplate = multiLingualTemplates.find(
        ({ lang }) => lang === to || (to === 'tl' && lang === 'ph'),
      );

      if (targetRecommendTemplate) {
        const { replyTemplate } = targetRecommendTemplate;

        const recommendTemplate = mustache2SlateValueWithCatch(
          replyTemplate.replace(/^#mustache#/, ''),
        );

        if (recommendTemplate) {
          return Promise.resolve(recommendTemplate);
        }
      }
    }
  }

  interface TextConcepts {
    text: string;
    concepts: ConceptElement[];
  }

  const getConceptHolder = (index: number) => `@x${index}`;

  const getTextConceptsList = (
    segments: Descendant[],
    res: TextConcepts[] = [],
  ): TextConcepts[] => {
    return segments.reduce((res, cur) => {
      if (cur.type === CustomSlateNodeType.PARAGRAPH) {
        return getTextConceptsList(cur.children, res);
      }

      const pushEmpty = () => res.push({ text: '', concepts: [] });

      if (cur.type === CustomSlateNodeType.DIVIDING_LINE) {
        pushEmpty();

        return res;
      }

      if (Text.isText(cur)) {
        if (res.length === 0) {
          pushEmpty();
        }

        const last = res[res.length - 1];

        last.text += cur.text;
      }

      if (cur.type === CustomSlateNodeType.CONCEPT) {
        if (res.length === 0) {
          pushEmpty();
        }

        const last = res[res.length - 1];

        const { concepts } = last;

        last.text += getConceptHolder(concepts.length);

        last.concepts.push(cur);
      }

      return res;
    }, res);
  };

  const textConceptsList = getTextConceptsList(template);

  return Promise.all(
    textConceptsList.map(({ text }) =>
      mode
        ? modeTranslate(text, to, mode, options)
        : overseaTranslate(text, to, options?.from || 'translateTool'),
    ),
  ).then((translatedTextList) => {
    const paragraphs = translatedTextList.map<ParagraphElement>((text, index) => {
      const { concepts } = textConceptsList[index];

      if (concepts.length === 0) {
        return {
          type: CustomSlateNodeType.PARAGRAPH,
          children: [
            {
              type: CustomSlateNodeType.TEXT,
              text,
            },
          ],
        };
      }

      const conceptWithPositionList = concepts.map((concept, index) => {
        const position = text.indexOf(getConceptHolder(index));

        return {
          concept,
          position: position === -1 ? Infinity : position,
          index,
        };
      });

      const sortedConceptWithPositionList = conceptWithPositionList.sort(
        (a, b) => a.position - b.position,
      );

      let restText = text;

      const children = sortedConceptWithPositionList.reduce<ParagraphElement['children']>(
        (res, conceptWithPosition, i) => {
          const { concept, index } = conceptWithPosition;

          const [preText, nextText = ''] = restText.split(getConceptHolder(index));

          if (preText) {
            res.push({
              type: CustomSlateNodeType.TEXT,
              text: preText,
            });
          }

          res.push(concept);

          if (i === concepts.length - 1) {
            res.push({
              type: CustomSlateNodeType.TEXT,
              text: nextText,
            });
          }

          restText = nextText;

          return res;
        },
        [],
      );

      return {
        type: CustomSlateNodeType.PARAGRAPH,
        children,
      };
    });

    return paragraphs.reduce<Descendant[]>((res, cur, index) => {
      res.push(cur);

      if (index < paragraphs.length - 1) {
        res.push({
          type: CustomSlateNodeType.DIVIDING_LINE,
          children: [{ text: '' }],
        });
      }

      return res;
    }, []);
  });
}

export function recommendTemplatesNew2Old(
  recommendTemplates?: Aide.RecommendTemplate[],
): Marketing.RecommendTemplate[] | undefined {
  return recommendTemplates?.map((recommendTemplate) => {
    const { lang, tag, tagTranslateMap, template, templateTranslateMap } = recommendTemplate;

    return {
      multiLingualTemplates: [
        {
          lang,
          tag,
          replyTemplate: template,
        },
        ...Object.keys(templateTranslateMap).map((lang) => {
          return {
            lang,
            tag: tagTranslateMap[lang] || '',
            replyTemplate: templateTranslateMap[lang] || '',
          };
        }),
      ],
    };
  });
}
